export const PAYMENTS_TEXT = Object.freeze({
  planModal: {
    sections: {
      general: {
        title: 'Información general',
        description: 'Comprobante, montos e impuestos.',
      },
      billing: {
        title: 'Facturación',
        description: 'Liga factura o referencia externa.',
      },
      details: {
        title: 'Detalle',
        description: 'Comentarios internos.',
      },
    },
    documentTypes: {
      cfdi: 'CFDI',
      simpleNote: 'Nota simple',
    },
    common: {
      noOption: 'Sin opción disponible',
    },
    modal: {
      closeAriaLabel: 'Cerrar modal',
      title: {
        create: 'Nuevo plan de pagos',
        edit: 'Editar plan de pagos',
      },
      description: {
        create: 'Captura rápida para registrar un plan de pagos.',
        edit: 'Actualiza la información del plan de pagos seleccionado.',
      },
      submit: {
        create: 'Guardar',
        edit: 'Actualizar',
      },
      buttons: {
        cancel: 'Cancelar',
        submitting: 'Guardando...',
      },
    },
    overrunConfirm: {
      title: 'Confirmar sobreasignación',
      subtitle: 'El monto supera el saldo pendiente del negocio.',
      confirm: 'Sí, guardar',
      cancel: 'No, revisar',
      message: ({ saleTotal, totalPlan, exceeded, projected }) => `Monto del negocio actual: ${saleTotal}\n+ Monto total del plan: ${totalPlan}\n+ Monto excedido: ${exceeded}\n= Nuevo monto del negocio: ${projected}\n\n¿Deseas continuar?`,
    },
    validation: {
      conceptRequired: 'Captura el concepto del plan de pagos.',
      amountGreaterThanZero: 'El monto debe ser mayor a 0.',
      recurringCountGreaterThanZero: 'La cantidad de planes de pago debe ser mayor a 0.',
      firstInstallmentRequired: 'Selecciona la primer mensualidad.',
      periodicityRequired: 'Selecciona la periodicidad del plan de pagos.',
      customerRequired: 'Selecciona un cliente para el plan de pagos.',
    },
    fallback: {
      customerById: ({ id }) => `Cliente #${id}`,
      taxById: ({ id }) => `Impuesto #${id}`,
      taxGroupById: ({ id }) => `Grupo #${id}`,
      invoiceById: ({ id }) => `Factura #${id}`,
      noFileSelected: 'Ningún archivo seleccionado',
    },
    general: {
      heading: {
        title: 'Información general',
        description: 'Configuración del comprobante, impuestos y montos base.',
      },
      status: {
        prefix: 'Estado',
        autoHelp: 'Este campo se actualiza automáticamente según el flujo del plan de pagos.',
        pendingFallback: 'Pendiente',
      },
      fields: {
        documentType: 'Tipo de comprobante *',
        customer: 'Cliente *',
        customerPlaceholder: 'Selecciona cliente',
        paymentAmountsTitle: 'Importes de plan de pagos',
        paymentAmountsSubtitle: 'Montos base y resumen de impuestos.',
        frequencyLabel: 'Frecuencia del plan de pagos',
        recurringCount: 'Cantidad de planes de pago',
        recurringCountPlaceholder: '1',
        firstInstallment: 'Primer mensualidad',
        firstInstallmentPlaceholder: 'Selecciona mes',
        periodicity: 'Periodicidad del plan de pagos',
        periodicityPlaceholder: 'Selecciona periodicidad',
        subtotal: 'Subtotal *',
        percent: 'Porcentaje %',
      },
      frequencyOptions: {
        unique: 'Único',
        monthly: 'Mensual',
        multiple: 'Múltiple',
      },
      taxes: {
        title: 'Impuestos',
        subtitle: 'Selecciona el tipo de impuestos aplicables para este plan de pagos.',
        withoutTaxes: 'Sin impuestos',
        taxType: 'Tipo de impuestos',
        taxes: 'Impuestos',
        placeholders: {
          taxes: 'Selecciona impuestos',
          group: 'Selecciona grupo',
        },
        fallbackTaxLabel: ({ id }) => `Impuesto #${id}`,
        fallbackGroupLabel: 'Grupo',
        fallbackIndividualLabel: 'Individual',
      },
      warnings: {
        exceededAmount: ({ amount }) => `Se ha superado el saldo pendiente del negocio en: ${amount}`,
        pendingAmount: ({ pending, maxPerPlan }) => `Saldo pendiente del negocio: ${pending} | Máximo por plan: ${maxPerPlan}`,
        exceededPercent: ({ percent }) => `Se ha superado el saldo pendiente del negocio en: ${percent}%`,
        pendingPercent: ({ pendingPercent, maxPercent }) => `Saldo pendiente del negocio (%): ${pendingPercent}% | Máximo por plan (%): ${maxPercent}%`,
      },
    },
    schedule: {
      fields: {
        invoiceDate: 'Fecha de Facturación',
        paymentDate: 'Fecha de pago',
        concept: 'Concepto *',
      },
      placeholders: {
        date: 'Selecciona fecha',
        concept: 'Concepto del plan de pagos',
      },
      nowButton: 'Hoy',
    },
    billing: {
      heading: {
        title: 'Facturación',
        description: 'Liga una factura del sistema o captura referencia externa.',
      },
      status: {
        unbilled: 'Sin facturar',
        orderly: 'Facturado en orderly',
        external: 'Facturado en otro sistema',
      },
      fields: {
        invoice: 'Factura',
        invoicePlaceholder: 'Selecciona una factura',
        invoiceXml: 'XML de la factura',
      },
      actions: {
        uploadXml: 'Subir XML',
      },
    },
    details: {
      heading: {
        title: 'Detalles',
        description: 'Organiza el plan de pagos con notas internas.',
      },
      fields: {
        area: 'Área',
        areaPlaceholder: 'Selecciona área',
        comments: 'Comentarios',
      },
      placeholders: {
        comments: 'Comentarios del plan de pagos',
      },
    },
  },
  toolbar: {
    searchPlaceholder: 'Buscar por concepto, factura o pago',
    emptyOption: 'Sin opción disponible',
    dateRangePresets: [
      { key: 'this_month', label: 'Este mes' },
      { key: 'last_month', label: 'Mes anterior' },
      { key: 'this_year', label: 'Año actual' },
      { key: 'last_year', label: 'Año anterior' },
    ],
    customer: {
      placeholder: 'Cliente',
      allLabel: 'Cliente: Todos',
    },
    billed: {
      placeholder: 'Facturado',
      singleLabelPrefix: 'Facturación:',
      options: {
        all: 'Todos',
        yes: 'Sí',
        no: 'No',
      },
    },
  },
  summary: {
    sections: {
      paymentPlan: 'Plan de pagos',
      paid: 'Pagado',
      pending: 'Pendiente',
      paidDone: 'Ya pagado',
      pendingToPay: 'Pendiente por pagar',
    },
    breakdown: {
      subtotal: 'Subtotal',
      transferredTaxes: 'Imp. Trasladados',
      withheldTaxes: 'Imp. Retenidos',
      total: 'Total',
    },
    metrics: {
      total: 'Plan de pagos',
      paid: 'Pagado',
      pending: 'Pendiente',
    },
    actions: {
      clear: 'Limpiar plan de pagos',
      newPaymentPlan: 'Nuevo plan de pagos',
    },
  },
  table: {
    loading: 'Cargando planes de pago...',
    empty: 'Sin planes de pago disponibles para los filtros seleccionados.',
    sortTitles: {
      paymentDate: 'Ordenar por fecha de pago',
      customer: 'Ordenar por cliente',
      subtotal: 'Ordenar por subtotal',
      total: 'Ordenar por total',
    },
    columns: {
      paymentDate: 'FECHA DE PAGO',
      customer: 'CLIENTE',
      concept: 'CONCEPTO',
      subtotal: 'SUBTOTAL',
      taxes: 'IMPUESTOS',
      total: 'TOTAL',
      invoice: 'FACTURA',
      payment: 'PAGO',
      paymentStatus: 'ESTADO DE PAGO',
    },
    links: {
      customerDetail: 'Ir al detalle del cliente',
      invoiceDetail: 'Ir al detalle de la factura',
      invoiceDefaultLabel: 'Ver factura',
    },
    actions: {
      issueInvoice: 'Facturar',
      issueSimpleNote: 'Nota simple',
      pay: 'Pagar',
      edit: 'Editar',
      delete: 'Eliminar',
      more: 'Más acciones',
      clonePayment: 'Clonar pago',
      creditNote: 'Nota de crédito',
      viewInvoice: 'Ver factura',
    },
    status: {
      paid: 'Pagado',
      pending: 'Pendiente',
    },
    taxes: {
      transferred: 'Trasladados',
      withheld: 'Retenidos',
    },
    exchangeRatePrefix: 'TC:',
  },
  payModal: {
    title: 'Registrar pago',
    subtitle: 'Captura la información del pago para este plan.',
    closeAriaLabel: 'Cerrar modal',
    voucherTypes: {
      cfdi: 'CFDI',
      simpleNote: 'Nota simple',
      foreignReceipt: 'Comprobante extranjero',
      payroll: 'Nómina',
    },
    planCard: {
      title: 'Plan de pago',
      subtitle: 'Información del plan de pagos seleccionado.',
      fields: {
        concept: 'Concepto',
        customer: 'Cliente',
        voucherType: 'Tipo de comprobante',
        scheduledDate: 'Fecha programada',
        paymentMethod: 'Método de pago',
      },
      voucherTypes: {
        simpleNote: 'Nota simple',
        cfdi: 'CFDI',
      },
    },
    expenseFeeCard: {
      title: 'Información general',
      fields: {
        supplier: 'Proveedor',
        collaborator: 'Colaborador',
        voucherType: 'Tipo de comprobante',
        scheduledDate: 'Fecha programada',
      },
    },
    billingData: {
      title: 'Datos de facturación',
      fields: {
        selector: 'Datos de facturación *',
        customer: 'Cliente',
        taxRegime: 'Régimen fiscal',
      },
      placeholders: {
        selector: 'Selecciona datos de facturación',
      },
      emptyOption: 'Sin opción disponible',
      emptyState: 'Selecciona un dato de facturación para cargar la información.',
      validation: {
        receiverRequiredToEdit: 'Selecciona un dato de facturación para editar.',
      },
      toasts: {
        loadContributorError: 'No se pudo cargar el dato de facturación.',
        updateContributorError: 'No se pudo actualizar el dato de facturación.',
        updateContributorSuccess: 'Dato de facturación actualizado correctamente.',
      },
    },
    amountBox: {
      title: 'Importes del pago',
      subtitle: 'Captura el importe y fecha efectiva del pago.',
      fields: {
        amount: 'Monto a pagar *',
        paymentDate: 'Fecha de pago *',
      },
      placeholders: {
        paymentDate: 'Selecciona fecha',
      },
      nowButton: 'Hoy',
      summary: {
        planAmount: 'Monto del plan',
        expenseAmount: 'Monto del gasto',
        feeAmount: 'Monto de la comisión',
        payAmount: 'Monto a pagar',
        pendingBalance: 'Saldo pendiente',
      },
    },
    banking: {
      title: 'Información bancaria',
      subtitle: 'Selecciona la forma y cuenta de pago.',
      subtitleAccountOnly: 'Selecciona la cuenta de pago.',
      fields: {
        banks: 'Bancos',
        paymentMethod: 'Forma de pago *',
        paymentAccount: 'Cuenta de pago',
        operationNumber: 'No. Operación',
        beneficiaryBankRfc: 'RFC del banco beneficiario',
        beneficiaryAccountNumber: 'Número de cuenta beneficiario',
        orderingBank: 'Banco ordenante',
        orderingBankRfc: 'RFC del banco ordenante',
        orderingBankAccount: 'Número de cuenta ordenante',
      },
      placeholders: {
        paymentMethod: 'Selecciona forma de pago',
        paymentAccount: 'Selecciona cuenta de pago',
        operationNumber: 'Ej. OP-240329-01',
        beneficiaryBankRfc: 'Captura RFC del banco beneficiario',
        beneficiaryAccountNumber: 'Captura número de cuenta beneficiario',
        orderingBank: 'Captura banco ordenante',
        orderingBankRfc: 'Captura RFC del banco ordenante',
        orderingBankAccount: 'Captura número de cuenta ordenante',
      },
      emptyOption: 'Sin opción disponible',
      unassignedAccount: 'Sin cuenta asignada',
    },
    validation: {
      amountGreaterThanPlan: 'No puedes pagar un monto mayor al del plan de pagos.',
      amountGreaterThanExpense: 'No puedes pagar un monto mayor al del gasto.',
      amountGreaterThanFee: 'No puedes pagar un monto mayor al de la comisión.',
    },
    warnings: {
      splitPlan: 'El plan de pagos se dividirá y se creará uno nuevo con el monto faltante.',
      splitExpense: 'El gasto se dividirá y se creará uno nuevo con el monto faltante.',
      splitFee: 'La comisión se dividirá y se creará una nueva con el monto faltante.',
    },
    buttons: {
      cancel: 'Cancelar',
      submit: 'Registrar pago',
      submitCfdi: 'Registrar Pago con CFDI',
      submitting: 'Guardando...',
    },
    fallback: {
      emptyValue: '-',
    },
  },
  tab: {
    confirms: {
      delete: {
        title: 'Eliminar plan de pago',
        subtitle: 'Esta acción no se puede deshacer.',
        cancel: 'Cancelar',
        confirm: 'Eliminar',
        confirming: 'Eliminando...',
      },
      clear: {
        title: 'Limpiar plan de pagos',
        subtitle: 'Se eliminarán únicamente planes no facturados y no pagados.',
        confirm: 'Limpiar plan de pagos',
        message:
          'Esta acción eliminará del negocio solo los planes de pago que no estén facturados ni pagados.\nLos planes facturados o pagados no se eliminarán.',
      },
      deleteMessageDefault: 'Confirma que deseas eliminar este plan de pago.',
      deleteSummary: ({ customer, concept, amount }) => `Cliente: ${customer}\nConcepto: ${concept}\nMonto: ${amount}`,
      noCustomer: 'Sin cliente',
      noConcept: 'Sin concepto',
    },
    statusTabs: {
      all: 'Todos',
      pending: 'Pendiente',
      paid: 'Pagado',
    },
    toasts: {
      infoSimpleNote: ({ id }) => `Emitir nota simple para plan #${id} estará disponible pronto.`,
      infoDuplicate: ({ id }) => `Duplicar pago #${id} estará disponible pronto.`,
      infoCreditNote: ({ id }) => `Nota de crédito para plan #${id} estará disponible pronto.`,
      infoNoInvoice: 'Este plan de pago no tiene factura registrada.',
      errorUnknownPlanToPay: 'No se identificó el plan de pago a liquidar.',
      successPayPlan: ({ amount }) => `Pago registrado correctamente (${amount}).`,
    },
    errors: {
      updatePlan: 'No se pudo actualizar el plan de pagos.',
      createPlan: 'No se pudo registrar el plan de pagos.',
      savePlan: 'No se pudo guardar el plan de pagos.',
      clearPlans: 'No se pudo limpiar el plan de pagos.',
      payPlan: 'No se pudo registrar el pago del plan.',
      loadPlanDetail: 'No se pudo cargar el detalle del plan de pagos.',
      loadPlan: 'No se pudo cargar el plan de pagos.',
      deletePlan: 'No se pudo eliminar el plan de pagos.',
    },
    success: {
      updatePlan: 'Plan de pagos actualizado correctamente.',
      createPlan: 'Plan de pagos registrado correctamente.',
      clearPlans: 'Los planes de pago no facturados ni pagados fueron eliminados correctamente.',
      deletePlan: 'El plan de pago fue eliminado correctamente.',
    },
  },
});
